import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { goToAiClasses } from '../../Utilities/aiHandoff';
import './HeroBanner.css';

const slides = [
  { 
    image: require('../../Assets/hero1.png'), 
    title: "Vipindi vya Video vya Walimu Bingwa", 
    subTitle: "Jifunza kutoka kwa walimu bora kupitia video zilizotengenezwa kitaalamu",
    accentColor: "#10B981",
    ctaText: "Anza Kujifunza",
    ctaPath: "/courses",
  },
  { 
    image: require('../../Assets/hero3.png'), 
    title: "Mitihani ya Kujitathimini", 
    subTitle: "Fuatilia maendeleo yako kwa maoni ya papo hapo na ufahamu wa kibinafsi",
    accentColor: "#10B981",
    ctaText: "Jaribu Mitihani",
    ctaPath: "/exam-system",
  },
  { 
    image: require('../../Assets/hero2.png'), 
    title: "AI, Assessment Intelligence", 
    subTitle: "Ufahamu unaoendeshwa na AI kuhusu mifumo yako ya kipekee ya kujifunza na uwezo wako",
    accentColor: "#10B981",
    ctaText: "Chunguza AI",
    ctaPath: "/ai-assessment",
    isAiHandoff: true,
  }
];

function HeroBanner({ isLoggedIn }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (isHovered) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide, isHovered]);

  const handleCTAClick = (slide) => {
    if (slide.isAiHandoff) {
      if (!isLoggedIn) {
        navigate("/signin?redirect=ai");
      } else {
        goToAiClasses();
      }
      return;
    }

    if (!isLoggedIn) {
      navigate("/signin");
    } else {
      navigate(slide.ctaPath);
    }
  };

  const handleGetStartedClick = () => {
    navigate("/signUp");
  };

  return (
    <div className="hero-wrapper">
      <div 
        className='hero-container' 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background Slides */}
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentSlide}
            className="hero-bg-slide"
            style={{ 
              backgroundImage: `url(${slides[currentSlide].image})`,
              '--accent-color': slides[currentSlide].accentColor
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          />
        </AnimatePresence>

        {/* Gradient Overlay */}
        <div className='hero-gradient-overlay' />
        
        {/* Content Container with white background and shadow */}
        <div className='hero-content-wrapper'>
          <div className='hero-content-container'>
            {/* Content */}
            <div className='hero-content'>
              <AnimatePresence mode='wait'>
                <motion.div
                  key={currentSlide}
                  className='hero-text-content'
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 className='hero-title'>
                    {slides[currentSlide].title}
                  </h1>
                  <p className='hero-subtitle'>
                    {slides[currentSlide].subTitle}
                  </p>
                  
                  <div className='hero-cta-buttons'>
                    {/* Primary CTA - Different for each slide */}
                    <motion.button
                      className='hero-cta-button primary'
                      onClick={() => handleCTAClick(slides[currentSlide])}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        backgroundColor: slides[currentSlide].accentColor
                      }}
                    >
                      <span className="cta-text">{isLoggedIn ? slides[currentSlide].ctaText : slides[currentSlide].ctaText}</span>
                      <svg className="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.button>

                    {/* Secondary CTA - Get Started (only for non-logged in users) */}
                    {/* {!isLoggedIn && (
                      <motion.button
                        className='hero-cta-button secondary'
                        onClick={handleGetStartedClick}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="cta-text">Jisajili Sasa</span>
                        <svg className="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </motion.button>
                    )} */}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <button className="slide-nav prev-slide" onClick={prevSlide}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" color='black'>
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="slide-nav next-slide" onClick={nextSlide}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" color='black'>
            <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Slide Navigation Dots */}
        {/* <div className="slide-dots-container">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`slide-dot ${currentSlide === index ? "active" : ""}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              <div className="dot-progress" />
            </button>
          ))}
        </div> */}

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel" />
          </div>
          <span className="scroll-text">Scroll to explore</span>
        </div>
      </div>

      {/* Stats Bar - Positioned below hero container */}
      <div className='hero-stats-container'>
        <div className='hero-stats'>
          <div className='stat-item'>
            <span className='stat-number'>10K+</span>
            <span className='stat-label'>Wanafunzi</span>
          </div>
          <div className='stat-divider' />
          <div className='stat-item'>
            <span className='stat-number'>98%</span>
            <span className='stat-label'>Wafanikiwa</span>
          </div>
          <div className='stat-divider' />
          <div className='stat-item'>
            <span className='stat-number'>500+</span>
            <span className='stat-label'>Masomo</span>
          </div>
          <div className='stat-divider' />
          <div className='stat-item'>
            <span className='stat-number'>24/7</span>
            <span className='stat-label'>Msaada</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroBanner;